import api from "./api";
import type {
  InventoryItem,
  CreateInventoryItemData,
  UpdateInventoryItemData,
} from "../types/models";

export interface InventoryStatusSummary {
  in_stock: number;
  low: number;
  out_of_stock: number;
}

export interface InventoryListParams {
  page?: number;
  limit?: number;
  category?: string[];
  status?: string[];
  search?: string | null;
  sort_by?: string;
  order?: string;
}

export interface InventoryListResponse {
  items: InventoryItem[];
  total: number;
  page: number;
  limit: number;
  pages: number;
  status_summary?: InventoryStatusSummary;
}

/**
 * Create a new inventory item.
 */
export async function createInventoryItem(data: CreateInventoryItemData): Promise<InventoryItem> {
  const response = await api.post<InventoryItem>("/inventory_items", data);
  return response.data;
}

/**
 * Get a single inventory item by ID.
 */
export async function getInventoryItem(id: string): Promise<InventoryItem> {
  const response = await api.get<InventoryItem>(`/inventory_items/${id}`);
  return response.data;
}

/**
 * Update an inventory item (partial update).
 */
export async function updateInventoryItem(
  id: string,
  data: UpdateInventoryItemData
): Promise<InventoryItem> {
  const response = await api.put<InventoryItem>(`/inventory_items/${id}`, data);
  return response.data;
}

/**
 * List inventory items with pagination, filters, and sort.
 */
export async function listInventoryItems(
  params: InventoryListParams = {}
): Promise<InventoryListResponse> {
  const {
    page = 1,
    limit = 20,
    category,
    status,
    search,
    sort_by = "status",
    order = "asc",
  } = params;

  const queryParams: Record<string, string | number | undefined> = {
    page,
    limit,
    sort_by,
    order,
  };
  if (category?.length) queryParams.category = category.join(",");
  if (status?.length) queryParams.status = status.join(",");
  if (search != null && search !== "") queryParams.search = search;

  const response = await api.get<InventoryListResponse>("/inventory_items", {
    params: queryParams,
  });
  return response.data;
}

/**
 * Quickly adjust inventory item quantity.
 * @param id - Item ID
 * @param adjustment - Relative change (+1 or -1), or
 * @param quantity - Absolute quantity to set
 */
export async function adjustQuantity(
  id: string,
  adjustment?: number,
  quantity?: number
): Promise<InventoryItem> {
  const body: { adjustment?: number; quantity?: number } = {};
  if (adjustment !== undefined) {
    body.adjustment = adjustment;
  } else if (quantity !== undefined) {
    body.quantity = quantity;
  }
  const response = await api.patch<InventoryItem>(`/inventory_items/${id}/quantity`, body);
  return response.data;
}

/**
 * Soft-delete an inventory item.
 * @param id - Item UUID
 * @throws Error if API request fails (404 → "Item not found", 403 → permission message)
 */
export async function deleteInventoryItem(id: string): Promise<void> {
  await api.delete(`/inventory_items/${id}`);
}

import type { InventorySummary } from "../types/models";

/**
 * Get inventory summary (total_items, status counts, total_value).
 */
export async function getInventorySummary(): Promise<InventorySummary> {
  const response = await api.get<InventorySummary>("/inventory_items/summary");
  return response.data;
}

export interface BulkThresholdUpdateItem {
  id: string;
  reorder_threshold: number;
}

/**
 * Bulk update reorder thresholds for multiple items.
 */
export async function bulkUpdateThresholds(
  items: BulkThresholdUpdateItem[]
): Promise<InventoryItem[]> {
  const response = await api.patch<InventoryItem[]>("/inventory_items/thresholds", items);
  return response.data;
}
